// src/pages/Help.js
import React, { useMemo, useState } from "react";
import Container from "../components/Container";
import { TfiHelpAlt } from "react-icons/tfi";
import { FaChevronDown, FaEnvelope, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import "./styles/help.css";

const Help = () => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState("ordering-1");

  const faqs = useMemo(
    () => [
      {
        group: "Ordering",
        items: [
          {
            id: "ordering-1",
            q: "How do I place an order?",
            a: `Browse products and open any item you want. Select options (size/color) if available, add to cart, then proceed to Checkout. 
                If your product requires printing, you’ll be prompted to upload your artwork/file during checkout or order request.`,
          },
          {
            id: "ordering-2",
            q: "Can I cancel or modify my order?",
            a: `Yes — you can request a change within 24 hours of placing the order (before production starts). 
                Contact us immediately with your Order Number.`,
          },
          {
            id: "ordering-3",
            q: "Do you accept custom print files (PDF, AI, CDR)?",
            a: `Yes. We accept print-ready files such as PDF, PNG, JPG, AI, and CDR depending on the product. 
                For best results, upload high-resolution artwork (300dpi) and include bleed if applicable.`,
          },
        ],
      },
      {
        group: "Shipping & Delivery",
        items: [
          {
            id: "shipping-1",
            q: "What are the shipping options and costs?",
            a: `We offer standard and express delivery. Costs depend on your location and package size. 
                You’ll see available shipping options at checkout.`,
          },
          {
            id: "shipping-2",
            q: "How can I track my order?",
            a: `Once your order is dispatched, we’ll share tracking details via email or phone (depending on what you provided).`,
          },
          {
            id: "shipping-3",
            q: "How long does production take?",
            a: `Production time varies by product and quantity. Most items take 1–3 working days after artwork approval. 
                Large quantities may take longer.`,
          },
        ],
      },
      {
        group: "Returns & Refunds",
        items: [
          {
            id: "returns-1",
            q: "What is your return policy?",
            a: `We accept returns within 5 days of delivery for eligible items that are unused and in original packaging.
                Custom printed items are only eligible if there is a production defect or incorrect delivery.`,
          },
          {
            id: "returns-2",
            q: "How long does it take to process a refund?",
            a: `Refunds are processed within 4 working days after inspection of the returned item. 
                You’ll receive confirmation once completed.`,
          },
        ],
      },
      {
        group: "Account & Payments",
        items: [
          {
            id: "account-1",
            q: "How do I create an account?",
            a: `Click “Sign Up” and fill in your details. You’ll get a confirmation message to verify your account.`,
          },
          {
            id: "account-2",
            q: "I forgot my password. What can I do?",
            a: `Click “Forgot Password” on the login page and follow the reset instructions sent to your email.`,
          },
          {
            id: "payment-1",
            q: "Which payment methods do you accept?",
            a: `We accept Visa/Mastercard, PayPal, and Mobile Money (where available). Payments are securely processed.`,
          },
        ],
      },
      {
        group: "Design & Print Guidelines",
        items: [
          {
            id: "design-1",
            q: "What file format is best for printing?",
            a: `PDF is best. If sending images, use PNG/JPG at 300dpi. For logos/vectors, AI/CDR is great.`,
          },
          {
            id: "design-2",
            q: "What is bleed and why does it matter?",
            a: `Bleed is extra space around your design so nothing gets cut off during trimming. 
                Many print products require 3mm bleed.`,
          },
          {
            id: "design-3",
            q: "Can Kupto help me design my artwork?",
            a: `Yes. If you need design support, contact us with your idea and product type — our team can assist.`,
          },
        ],
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;

    return faqs
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (x) =>
            x.q.toLowerCase().includes(q) || x.a.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [faqs, query]);

  const toggle = (id) => setOpen((prev) => (prev === id ? "" : id));

  return (
    <>
      

      <Container class1="help-wrapper">
        {/* Header */}
        <div className="help-hero">
          <div className="help-heroLeft">
            <p className="help-kicker">Help Center</p>
            <h1 className="help-title">Welcome to the Kupto Help Center</h1>
            <p className="help-sub">
              Find answers fast — or reach our team for help with orders, printing,
              delivery, and accounts.
            </p>

            {/* Search */}
            <div className="help-search">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions… (e.g. refund, delivery, file upload)"
              />
              <button type="button" onClick={() => setQuery("")}>
                Clear
              </button>
            </div>
          </div>

          <div className="help-heroRight">
            <div className="help-iconWrap">
              <TfiHelpAlt className="help-icon" />
            </div>

            <div className="help-hoursCard">
              <p className="help-cardTitle">Working Hours</p>
              <p className="help-cardLine">Monday – Saturday</p>
              <p className="help-cardLine">
                <strong>8:00 AM – 7:00 PM</strong>
              </p>
              <p className="help-cardNote">
                (If you message outside these hours, we’ll reply the next working day.)
              </p>
            </div>
          </div>
        </div>

        {/* Quick contact cards */}
        <div className="help-quick">
          <div className="help-qCard">
            <div className="help-qIcon">
              <FaEnvelope />
            </div>
            <div className="help-qBody">
              <p className="help-qTitle">Email</p>
              <p className="help-qText">kupto2020@gmail.com</p>
              <p className="help-qHint">Best for detailed requests & attachments</p>
            </div>
          </div>

          <div className="help-qCard">
            <div className="help-qIcon">
              <FaPhoneAlt />
            </div>
            <div className="help-qBody">
              <p className="help-qTitle">Phone</p>
              <p className="help-qText">+256 200 949 142</p>
              <p className="help-qHint">Calls during working hours</p>
            </div>
          </div>

          <div className="help-qCard">
            <div className="help-qIcon">
              <FaWhatsapp />
            </div>
            <div className="help-qBody">
              <p className="help-qTitle">WhatsApp</p>
              <p className="help-qText">+256 200 949 142</p>
              <p className="help-qHint">Fastest response for quick questions</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="help-grid">
          <div className="help-faq">
            <div className="help-sectionHead">
              <h2>Frequently Asked Questions</h2>
              <p>Tap a question to expand.</p>
            </div>

            {filteredFaqs.map((group) => (
              <div className="help-group" key={group.group}>
                <h3 className="help-groupTitle">{group.group}</h3>

                {group.items.map((item) => {
                  const isOpen = open === item.id;
                  return (
                    <div className={`help-acc ${isOpen ? "is-open" : ""}`} key={item.id}>
                      <button
                        type="button"
                        className="help-accBtn"
                        onClick={() => toggle(item.id)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.q}</span>
                        <FaChevronDown className="help-chevron" />
                      </button>

                      <div className="help-accBody">
                        <p>{item.a}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="help-empty">
                No results found. Try a different keyword (e.g. “refund”, “shipping”, “file”).
              </div>
            )}
          </div>

          {/* Side panel */}
          <aside className="help-side">
            <div className="help-sideCard">
              <h3>Before you contact us</h3>
              <ul>
                <li>Have your <strong>Order Number</strong> ready (if available).</li>
                <li>For print issues, attach your <strong>artwork file</strong> or screenshots.</li>
                <li>Tell us product name, quantity, and delivery location.</li>
              </ul>
            </div>

            <div className="help-sideCard">
              <h3>Print File Tips</h3>
              <ul>
                <li>Use <strong>PDF</strong> for best print output.</li>
                <li>Images should be <strong>300dpi</strong>.</li>
                <li>Use <strong>CMYK</strong> color mode when possible.</li>
                <li>Add <strong>3mm bleed</strong> for trimmed products.</li>
              </ul>
            </div>

            <div className="help-sideCard help-sideCard--address">
              <h3>Our Address</h3>
              <p>
                P.O Box 9696 <br />
                Nasser Road, Light Arcade <br />
                Room No. B14 <br />
                Kampala, Uganda
              </p>
            </div>
          </aside>
        </div>
      </Container>
    </>
  );
};

export default Help;
